import { JSX } from 'react';
import { twMerge } from 'tailwind-merge';

import { getAccessibleFocusStyles } from '@videoblocks/design-system';
import { ChevronLeft, ChevronRight } from '@videoblocks/react-icons';

export type PaginationProps = {
    resultsPerPage: number;
    totalResults: number;
    goTo: (page: number) => void;
    currentPage: number;
    darkMode?: boolean;
};

/*  helpers  */
const range = (start: number, end: number) => Array.from({ length: end - start }, (_, i) => i + start);

const setPaginationRange = (
    current: number,
    total: number,
    visible = 5, // how many buttons to keep in the bar
) => {
    const start = Math.max(1, Math.min(current - Math.floor((visible - 3) / 2), total - visible + 2));
    const end = Math.min(total, Math.max(current + Math.floor((visible - 2) / 2), visible - 1));

    return [
        ...(start > 2 ? [1, '…'] : start > 1 ? [1] : []),
        ...range(start, end + 1),
        ...(end < total - 1 ? ['…', total] : end < total ? [total] : []),
    ];
};

export default function Pagination({
    resultsPerPage,
    totalResults,
    currentPage,
    goTo,
    darkMode = false,
}: PaginationProps): JSX.Element {
    const maxPage = Math.max(1, Math.ceil(totalResults / resultsPerPage));

    /*  Build the button list */
    const pages = setPaginationRange(currentPage, maxPage);

    const baseBtn = twMerge(
        'flex items-center justify-center w-12 h-12 text-sm font-bold rounded-sm',
        getAccessibleFocusStyles(),
        darkMode ? '' : 'text-gray-900 hover:bg-gray-200',
    );
    const disabledBtn = twMerge('pointer-events-none', darkMode ? '' : 'opacity-50');
    const activeCircle = twMerge(
        'flex items-center justify-center w-7 h-7 rounded-full',
        darkMode ? 'bg-white text-gray-900' : 'bg-gray-900 text-white',
        'pointer-events-none',
    );

    return (
        <div className="flex justify-center mt-12 w-full">
            <div
                className={twMerge(
                    'flex items-center px-4 py-2 rounded-md border border-gray-400',
                    darkMode && 'text-white bg-gray-900',
                )}
            >
                {/* PREV */}
                {(!darkMode || currentPage !== 1) && (
                    <button
                        aria-label="Previous page"
                        onClick={() => goTo(currentPage - 1)}
                        className={twMerge(baseBtn, currentPage === 1 ? disabledBtn : 'cursor-pointer')}
                        disabled={currentPage === 1}
                        tabIndex={currentPage === 1 ? -1 : 0}
                    >
                        <ChevronLeft className="w-4 h-4 fill-current" />
                    </button>
                )}

                {/* PAGE BUTTONS / ELLIPSES */}
                {pages.map((p, i) =>
                    p === '…' ? (
                        <div key={`ellipsis-${i}`} className="flex items-center justify-center w-4 h-12 text-current">
                            &hellip;
                        </div>
                    ) : (
                        <button
                            key={p}
                            onClick={() => goTo(p as number)}
                            disabled={p === currentPage}
                            className={`${baseBtn} ${p === currentPage ? disabledBtn : 'cursor-pointer'}`}
                        >
                            <span className={p === currentPage ? activeCircle : undefined}>{p}</span>
                        </button>
                    ),
                )}

                {/* NEXT */}
                {(!darkMode || currentPage !== maxPage) && (
                    <button
                        aria-label="Next page"
                        onClick={() => goTo(currentPage + 1)}
                        className={`${baseBtn} ${currentPage === maxPage ? disabledBtn : 'cursor-pointer'}`}
                        disabled={currentPage === maxPage}
                        tabIndex={currentPage === maxPage ? -1 : 0}
                    >
                        <ChevronRight className="w-4 h-4 fill-current" />
                    </button>
                )}
            </div>
        </div>
    );
}
